import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Image } from '@ms/core';
import { KeyValueDTO } from '@ms/core/model/keyvalue.model';
import { HTTP_CLIENT_XHR } from 'app/system/http-client-provider';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MentoringPlanReportSearch } from '../education/reports/mentoring-plan-report/mentoring-plan-report.model';
import { EmployeePosition } from '../employee-position/employee-position.model';
import { Employee, EmployeeProfileInfo, TeachersList } from '../employees';
import { EmployeeList, EmployeeSearch } from '../employees/employee-list/model/employee-search.model';
import { EmployeeType } from '../employees/enum/employeeType.enum';
import { UserAttachment } from '../shared/attachments/user-attachment.model';
import { hideProgress, logRequest } from '../shared/http-utils';
import { IdCardSearch } from '../shared/model/id-card-search';
import { UserAccountCreateDTO } from '../shared/model/user-account-create.model';
import { EmployeeAutocompleteSearchDTO } from './../shared/model/employee-search.model';
import { AttachmentService } from './attachment.service';
import { SchoolService } from './school.service';
@Injectable({
    providedIn: 'root',
})
export class EmployeeService implements AttachmentService {
    baseUrl = '/school-service/api/employee';

    xhrHttp = inject(HTTP_CLIENT_XHR);
    constructor(
        private http: HttpClient,
        private schoolService: SchoolService
    ) {}

    downloadFileByUrl(attachmentUrl: string) {
        return this.http.get(`${this.baseUrl}/attachmentUrl/${attachmentUrl}?time=${new Date().getTime()}`, {
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    searchEmployees(employeeSearchDTO: EmployeeSearch): Observable<HttpResponse<EmployeeList[]>> {
        employeeSearchDTO.schoolId = employeeSearchDTO.schoolId
            ? employeeSearchDTO.schoolId
            : this.schoolService.getActiveSchool().id;
        return this.http.post<EmployeeList[]>(`${this.baseUrl}/search`, employeeSearchDTO, { observe: 'response' });
    }

    searchEmployeesWithActivation(employeeSearchDTO: EmployeeSearch): Observable<HttpResponse<EmployeeList[]>> {
        employeeSearchDTO.schoolId = employeeSearchDTO.schoolId
            ? employeeSearchDTO.schoolId
            : this.schoolService.getActiveSchool().id;
        return this.http.post<EmployeeList[]>(`${this.baseUrl}/search-with-activation`, employeeSearchDTO, {
            observe: 'response',
        });
    }

    exportXLS(searchDTO: EmployeeSearch): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/export/xls`, searchDTO, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    uploadPhotos(schoolId: string, formData: FormData) {
        return this.xhrHttp.post<any>(`${this.baseUrl}/upload/photos/${schoolId}`, formData, {
            observe: 'response',
            reportProgress: true,
        });
    }

    getCourseEventsAvailableTeachers(
        courseEventIds: string[],
        onlyFreeTeachers: boolean
    ): Observable<HttpResponse<EmployeeList[]>> {
        return this.http.post<EmployeeList[]>(
            `${this.baseUrl}/available-teachers/${onlyFreeTeachers}`,
            courseEventIds,
            {
                observe: 'response',
            }
        );
    }

    getCourseEventsRemovableTeachers(courseEventIds: string[]): Observable<HttpResponse<EmployeeList[]>> {
        return this.http.post<EmployeeList[]>(`${this.baseUrl}/removable-teachers`, courseEventIds, {
            observe: 'response',
        });
    }

    getCoursesEventTeachers(courseEventIds: string[]): Observable<HttpResponse<Employee[]>> {
        return this.http.post<Employee[]>(`${this.baseUrl}/search-course-teachers`, courseEventIds, {
            observe: 'response',
        });
    }

    getEmployees(): Observable<Employee[]> {
        return this.http.get<Employee[]>(
            `/school-service/api/school/${this.schoolService.getActiveSchool().id}/employee`
        );
    }

    getActiveEmployees(): Observable<Employee[]> {
        return this.http.get<Employee[]>(
            `/school-service/api/school/${this.schoolService.getActiveSchool().id}/employee/active`
        );
    }

    getEmployeeById(id: string, progressHidden = false): Observable<HttpResponse<Employee>> {
        return this.http.get<Employee>(`${this.baseUrl}/${id}`, {
            observe: 'response',
            headers: progressHidden ? hideProgress() : null,
        });
    }

    getEmployeeByUserId(id: string, schoolId: string, fetchClassId: boolean): Observable<HttpResponse<Employee>> {
        return this.http.get<Employee>(
            `${this.baseUrl}/user-id/${id}/school/${schoolId}/fetch-class-id/${fetchClassId}`,
            {
                observe: 'response',
            }
        );
    }

    getEmployeeByUserIds(schoolId: string, ids: string[]): Observable<HttpResponse<Employee[]>> {
        return this.http.post<Employee[]>(`${this.baseUrl}/${schoolId}`, ids, { observe: 'response' });
    }

    createEmployee(employee: Employee): Observable<HttpResponse<Employee>> {
        return this.http.post<Employee>(this.baseUrl, employee, { observe: 'response', headers: logRequest() });
    }

    updateEmployee(employee: Employee): Observable<HttpResponse<Employee>> {
        return this.http.put<Employee>(this.baseUrl, employee, { observe: 'response', headers: logRequest() });
    }

    deleteEmployee(id: string): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.baseUrl}/${id}`, { observe: 'response', headers: logRequest() });
    }

    getEmployeesAutocomplete(text: string, type?: EmployeeType): Observable<HttpResponse<KeyValueDTO[]>> {
        const searchDTO = new EmployeeAutocompleteSearchDTO(text, this.schoolService.getActiveSchool().id, type);
        return this.http.post<KeyValueDTO[]>(`${this.baseUrl}/autocomplete`, searchDTO, { observe: 'response' }).pipe(
            map(results => results),
            catchError(() => [])
        );
    }

    map(response: Employee): Employee {
        const result: Employee = Object.assign({}, response);
        result.profile = Object.assign({}, result.profile);
        result.profile.address = Object.assign({}, result.profile.address);
        result.profile.address.state = Object.assign({ id: '' }, result.profile.address.state);
        result.profile.address.country = Object.assign({ id: '' }, result.profile.address.country);
        result.profile.documentInfo = Object.assign({}, result.profile.documentInfo);
        result.profile.contactInfo = Object.assign({}, result.profile.contactInfo);
        console.log('Map Result:', result);
        return result;
    }

    updateImage64(id: string, image64: string): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/image64/${id}`, image64, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getImage(id: string): Observable<Image> {
        return this.http.get<Image>(`${this.baseUrl}/${id}/image`);
    }

    getImagePath(id: string): Observable<Image> {
        return this.http.get<Image>(`${this.baseUrl}/${id}/image-path`);
    }

    connected(id: string): Observable<boolean> {
        return this.http.get<boolean>(`${this.baseUrl}/connection-check/${id}`);
    }

    deleteImage(employeeId: string): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.baseUrl}/${employeeId}/image`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    getAttachments(entityId: string) {
        return this.http.get(`${this.baseUrl}/${entityId}/attachment`);
    }

    deleteFile(entityId: string, attachmentTypeId: string) {
        return this.http.delete(`${this.baseUrl}/${entityId}/attachment/${attachmentTypeId}`, {
            headers: logRequest(),
        });
    }

    uploadFile(entityId: string, id: string, formData: FormData): Observable<HttpResponse<UserAttachment>> {
        return this.http.post<UserAttachment>(`${this.baseUrl}/${entityId}/attachment/${id}`, formData, {
            observe: 'response',
        });
    }
    downloadFile(entityId: string, attachmentTypeId: string) {
        return this.http.get(`${this.baseUrl}/${entityId}/attachment/${attachmentTypeId}`, {
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    createAccounts(employeeIds: string[]): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/users`, employeeIds, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    createAccount(request: UserAccountCreateDTO) {
        return this.http.post(`${this.baseUrl}/user`, request, { observe: 'response', headers: logRequest() });
    }

    generateEmployeeCard(searchDTO: IdCardSearch): Observable<HttpResponse<any>> {
        return this.http.post(`${this.baseUrl}/generate-card`, searchDTO, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    downloadTemplate(): Observable<HttpResponse<any>> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get(`${this.baseUrl}/school/${schoolId}/download-template`, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }

    getEmployeesFromExcel(formData: FormData, schoolId: string) {
        return this.xhrHttp.post<any>(`${this.baseUrl}/upload-excel/${schoolId}`, formData, {
            observe: 'events',
            reportProgress: true,
        });
    }

    import(list: Employee[], existingFields: string[]) {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post<any>(
            `${this.baseUrl}/school/${schoolId}/import`,
            { list, existingFields },
            {
                observe: 'response',
                headers: logRequest(),
            }
        );
    }

    getEmployeeProfileInfo(): Observable<HttpResponse<EmployeeProfileInfo>> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get<EmployeeProfileInfo>(`${this.baseUrl}/profile-info/school/${schoolId}`, {
            observe: 'response',
        });
    }
    getUsersBySubjectId(schoolId: string, subjectId: string): Observable<HttpResponse<KeyValueDTO[]>> {
        return this.http.get<KeyValueDTO[]>(`${this.baseUrl}/school/${schoolId}/subject/${subjectId}`, {
            observe: 'response',
        });
    }

    downloadTeachersList(schoolId: string, academicYearId: string): Observable<HttpResponse<any>> {
        return this.http.get(
            `${this.baseUrl}/download-teachers-list/school/${schoolId}/academicYearId/${academicYearId}`,
            {
                observe: 'response',
                responseType: 'blob',
                headers: logRequest(),
            }
        );
    }

    getTeachersListReport(schoolId: string, academicYearId: string): Observable<HttpResponse<TeachersList[]>> {
        return this.http.get<TeachersList[]>(
            `${this.baseUrl}/teachers-list-report/school/${schoolId}/academicYearId/${academicYearId}`,
            {
                observe: 'response',
            }
        );
    }

    getEmployeePositions(id: string): Observable<HttpResponse<EmployeePosition[]>> {
        return this.http.get<EmployeePosition[]>(`${this.baseUrl}/${id}/positions`, {
            observe: 'response',
        });
    }

    downloadMentoringPlanReport(searchDTO: MentoringPlanReportSearch): Observable<HttpResponse<any>> {
        // searchDTO.schoolId = this.schoolService.getActiveSchool().id;
        return this.http.post(`${this.baseUrl}/mentoring-plan-report`, searchDTO, {
            observe: 'response',
            responseType: 'blob',
            headers: logRequest(),
        });
    }
}
